import { graphql } from 'react-relay';
import { useIntl } from 'react-intl';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';

import type { ContentReportTableItem, SearchQueryData } from '../contentReportTableTypes.ts';

import type { ContentReportTableMacroParamsForQuery } from './useParsedMacroParams.ts';
import { mapSearchNodeToItem } from './mapSearchNodeToItem.ts';

interface UseContentReportTableDataResult {
	items: ContentReportTableItem[] | undefined;
	loading: boolean;
	error: Error | null;
}

type ContentReportTableDataQueryVariables = ContentReportTableMacroParamsForQuery & {
	skipSearchTitle: boolean;
	skipContentTitle: boolean;
};

/**
 * Hook that fetches content matching the given CQL query via the
 * confluence_search GraphQL endpoint.
 */
export const useContentReportTableData = (
	params: ContentReportTableMacroParamsForQuery,
): UseContentReportTableDataResult => {
	const intl = useIntl();
	const { cql, first, includeComments, includeLikes } = params;

	const { data, loading, error } = useQueryInterop<
		SearchQueryData,
		ContentReportTableDataQueryVariables
	>(
		graphql`
			query useContentReportTableDataQuery(
				$cql: String!
				$first: Int
				$includeComments: Boolean!
				$includeLikes: Boolean!
				$skipSearchTitle: Boolean!
				$skipContentTitle: Boolean!
			) {
				search: confluence_search(cql: $cql, first: $first) {
					nodes {
						title @skip(if: $skipSearchTitle)
						url
						lastModified
						content {
							id
							title @skip(if: $skipContentTitle)
							type
							creatorId
							history {
								createdBy {
									displayName
								}
							}
							comments @include(if: $includeComments) {
								count
							}
							likes @include(if: $includeLikes) {
								count
							}
							space {
								key
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				cql,
				first,
				includeComments,
				includeLikes,
				skipSearchTitle: fg('cc_rewrite_macros_use_content_not_search_title'),
				skipContentTitle: !fg('cc_rewrite_macros_use_content_not_search_title'),
			},
			skip: !cql,
			fetchPolicy: 'cache-first',
		},
	);

	if (!data) {
		return { items: undefined, loading: loading ?? false, error: error ?? null };
	}

	const nodes = data.search?.nodes ?? [];
	const items: ContentReportTableItem[] = [];
	for (const node of nodes) {
		const item = mapSearchNodeToItem(node, intl);
		if (item !== null) {
			items.push(item);
		}
	}

	return { items, loading: loading ?? false, error: error ?? null };
};
